import { HOVER_CARD_EDGE_GAP, HOVER_CARD_SIDE_OFFSET } from '@seen/ui'
import { type RefObject, useCallback, useLayoutEffect, useState } from 'react'

// The widest each facesheet card can render, measured as the card's own border
// box — what the viewport clamp acts on — not the content inside it. The two
// content-capped cards add the card's p-4 (32) and border (2) to their cap:
// ClinicalHoverLayout stops at max-w-[360px], the medication history tooltip at
// max-w-xl (576). The observation card sets w-lg on the card itself, so
// that number already is the border box. The two header cards (Participant
// Details, Day Center Schedule) both render w-80 (320) plus the 2px border.
export const CLINICAL_HOVER_CARD_WIDTH = 394
export const MEDICATION_HOVER_CARD_WIDTH = 610
export const OBSERVATION_HOVER_CARD_WIDTH = 512
export const HEADER_HOVER_CARD_WIDTH = 322

export interface AdaptiveHoverCardPlacement {
  side: 'right'
  sideOffset: number
  onOpenChange: (open: boolean) => void
  cardRef: (node: HTMLElement | null) => void
}

// Every card opens to the right of the row; only the distance changes. With
// room to spare that is the usual 4px gap. Without it the offset goes negative
// and slides the card left over the rows until its right edge lands on the
// viewport's edge gap, which keeps the card at full width and leaves the left
// of each row — the names and dates — readable behind it.
//
// cardWidth is what the card will actually occupy. Before it exists that is the
// declared cap; once it has rendered it is the width it took, which is often
// less and would otherwise leave the card over-slid and its right edge short of
// the gap.
export function computeAdaptiveHoverCardOffset(
  rowRight: number,
  viewportWidth: number,
  cardWidth: number
): number {
  const fitted = viewportWidth - rowRight - HOVER_CARD_EDGE_GAP - cardWidth

  // Radix shifts a right-side card vertically but never horizontally, so an
  // offset that ran past the left edge would not be pulled back. Only reachable
  // in a window narrower than the card plus both edge gaps.
  return Math.max(
    Math.min(HOVER_CARD_SIDE_OFFSET, fitted),
    HOVER_CARD_EDGE_GAP - rowRight
  )
}

// Placed in two passes, both before the card is ever visible.
//
// The first runs on open, from the card's declared cap. Radix calls
// onOpenChange while setting its own open state, so this lands in the same
// render that mounts the content — the card is never laid out somewhere wildly
// wrong. The second runs from the mounted card's real width, which is the only
// moment it can be known and is usually narrower than the cap.
//
// The second pass is safe to do this late because Radix parks the popper at
// translate(0, -200%) with min-width max-content until floating-ui reports it
// positioned. The card is laid out at its natural width but off the page, so
// measuring it and correcting the offset both happen while it cannot be seen.
//
// Neither pass listens for resize; a window resized under an open card keeps
// the offset it opened with.
export function useAdaptiveHoverCardPlacement(
  anchorRef: RefObject<HTMLElement | null>,
  requiredWidth: number
): AdaptiveHoverCardPlacement {
  const [sideOffset, setSideOffset] = useState(HOVER_CARD_SIDE_OFFSET)

  const placeAgainst = useCallback(
    (cardWidth: number): void => {
      const anchor = anchorRef.current
      if (!anchor) {
        return
      }
      // A card that fills its cap resolves to the same number twice, so React
      // bails out and the resting case never re-renders at all.
      setSideOffset(
        computeAdaptiveHoverCardOffset(
          anchor.getBoundingClientRect().right,
          window.innerWidth,
          cardWidth
        )
      )
    },
    [anchorRef]
  )

  const onOpenChange = useCallback(
    (open: boolean): void => {
      if (open) {
        placeAgainst(requiredWidth)
      }
    },
    [placeAgainst, requiredWidth]
  )

  const [card, setCard] = useState<HTMLElement | null>(null)
  const cardRef = useCallback((node: HTMLElement | null): void => {
    setCard(node)
  }, [])

  // A layout effect rather than the ref callback itself: the callback runs as
  // the node is attached, when it can still measure 0, and a 0 here is not
  // harmless — it reads as a card needing no room, resting the offset at 4,
  // where the viewport clamp then starves the card to the sliver beside the
  // row. By the time layout effects run the tree is committed and the width is
  // real. Still before paint, so the correction is not visible.
  useLayoutEffect(() => {
    if (!card) {
      return
    }
    const width = card.getBoundingClientRect().width
    if (width > 0) {
      placeAgainst(width)
    }
  }, [card, placeAgainst])

  return { side: 'right', sideOffset, onOpenChange, cardRef }
}
